/**
 * M1 FIX (Mission 35B): Single source of truth for notification behavior types.
 * Imported by both notificationHelpers.ts (web) and nativeNotificationHelpers.ts (native)
 * to eliminate duplicated behavior matrices that could diverge silently.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Adding a new banner notification type — checklist:
 *   1. Add the type string to BANNER_TYPES below
 *   2. Add an SF Symbol name to NOTIFICATION_SF_SYMBOL_MAP below (iOS-native)
 *   3. If the SF Symbol isn't already mapped in `components/native/NativeIcon.tsx`
 *      (SF_TO_LUCIDE or SF_TO_MATERIAL), add the Lucide / Material fallback there
 *      so Android renders correctly.
 *   4. Backend emits the type via unified_notification_orchestrator — no
 *      frontend changes required beyond steps 1-3.
 * ───────────────────────────────────────────────────────────────────────────
 */

/** Notification types that auto-navigate to chat when user is NOT on chat screen */
export const AUTO_NAVIGATE_TYPES = ['post_workout_debrief'] as const;

/** Notification types that show a banner when user is NOT on chat screen */
export const BANNER_TYPES = [
  'morning_checkin',
  'workout_reminder',
  'goal_progress',
  'health_checkin',
  'plan_generated',
  'plan_updated',
  'weekly_plan_ready',
  'weekly_report',
  'discovery_adjustment_check_in',
  'goal_approaching_completion',
  'goal_completed',
  'goal_stall_check_in',
  'gentle_second_goal',
  'milestone_achievement',
  'referral_reward_ready',  // 🌞 Summer50: friend activated trial, reward claimable
  'background_response',
  'chat_response_ready',
  // W8 (2026-04-28): debrief banner restored. The 2026-03-29 commit
  // 275fc4eba intentionally removed the in-app pill assuming push
  // would always succeed — but push silently fails when the user
  // has no Expo token, leaving them with no notification at all.
  // The banner is the in-app fallback (push remains for backgrounded
  // app). Pairs with the channel-list update in
  // unified_notification_orchestrator.send_push_only_notification.
  // Refs: docs/plans/cross-device-pipeline-parity-sprint.md W8
  'post_workout_debrief',
  // ⏰ appointed-time-grove (2026-05-28): per-session reminder pushes.
  // Foreground pushes are OS-suppressed by design (nativePushNotifications
  // foreground handler), so without this entry the alarm the user set is
  // COMPLETELY SILENT whenever they happen to be in-app at the chosen
  // minute (forensic audit finding). The in-app banner is the foreground
  // delivery path; the OS push covers backgrounded/locked.
  'session_time_reminder',
] as const;

/**
 * SF Symbol name per notification type — used by the native in-app banner.
 * Values are passed straight to NativeIcon, which maps them to Lucide /
 * MaterialCommunityIcons on Android (see components/native/NativeIcon.tsx).
 *
 * Emojis are deliberately avoided here: per product direction, emojis are
 * reserved for Cedar's own conversational output. UI chrome (banners, pills,
 * badges) uses branded icons for a consistent visual language.
 *
 * Chat-response types (`chat_response_ready`, `background_response`) are a
 * special case — they render the branded ChatBubble "CC" chip instead of an
 * SF Symbol. That branch lives in nativeNotificationHelpers.getNativeNotificationIcon.
 */
export const NOTIFICATION_SF_SYMBOL_MAP: Record<string, string> = {
  morning_checkin: 'sun.max.fill',
  workout_reminder: 'figure.run',
  health_checkin: 'heart.fill',
  goal_progress: 'target',
  goal_completed: 'trophy.fill',
  goal_stall_check_in: 'arrow.triangle.2.circlepath',
  goal_approaching_completion: 'target',
  gentle_second_goal: 'sparkles',
  plan_generated: 'sparkles',
  plan_updated: 'arrow.triangle.2.circlepath',
  weekly_plan_ready: 'calendar',
  weekly_report: 'chart.bar.fill',
  post_workout_debrief: 'figure.strengthtraining.traditional',
  discovery_adjustment_check_in: 'bubble.left.fill',
  milestone_achievement: 'star.fill',
  referral_reward_ready: 'gift.fill',  // 🌞 Summer50
  // ⏰ appointed-time-grove: 'clock.fill' is already mapped to Lucide
  // Clock in NativeIcon.tsx SF_TO_LUCIDE — no Android fallback needed.
  session_time_reminder: 'clock.fill',
};

/** Fallback SF Symbol when a notification type isn't in the map above. */
export const DEFAULT_NOTIFICATION_ICON = 'bell.fill';
